// Blog Service for AllArtWeb
// Handles all blog post operations: create, read, update, delete
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from './config';

const BLOG_COLLECTION = 'blog_posts';

/**
 * Create a new blog post (Vendor)
 */
export const createBlogPost = async (postData) => {
  try {
    const newPostRef = doc(collection(db, BLOG_COLLECTION));
    
    const postWithMetadata = {
      ...postData,
      id: newPostRef.id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: 'pending', // pending, approved, rejected
      viewCount: 0,
      likes: 0,
      comments: 0,
    };

    await setDoc(newPostRef, postWithMetadata);
    return { success: true, id: newPostRef.id, data: postWithMetadata };
  } catch (error) {
    console.error('Error creating blog post:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update blog post (Vendor can only update own pending posts)
 */
export const updateBlogPost = async (postId, updates) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(postRef, updateData);
    return { success: true, data: updateData };
  } catch (error) {
    console.error('Error updating blog post:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete blog post (Vendor or Admin)
 */
export const deleteBlogPost = async (postId) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    await deleteDoc(postRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting blog post:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get single blog post by ID
 */
export const getBlogPost = async (postId) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      return { success: false, error: 'Post not found' };
    }

    return { success: true, data: postSnap.data() };
  } catch (error) {
    console.error('Error getting blog post:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all approved blog posts for public view
 */
export const getApprovedBlogPosts = async (pageSize = 12) => {
  try {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => doc.data());

    return { success: true, data: posts };
  } catch (error) {
    console.error('Error getting approved posts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get trending blog posts (most viewed) for homepage
 */
export const getTrendingBlogPosts = async (pageSize = 4) => {
  try {
    // Get all approved posts and sort in client-side to avoid composite index requirement
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
      limit(pageSize * 3) // Get more and filter client-side
    );

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs
      .map(doc => doc.data())
      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      .slice(0, pageSize);

    return { success: true, data: posts };
  } catch (error) {
    console.error('Error getting trending posts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get newest blog posts (for homepage or category)
 */
export const getNewestBlogPosts = async (pageSize = 4) => {
  try {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => doc.data());

    return { success: true, data: posts };
  } catch (error) {
    console.error('Error getting newest posts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get vendor's blog posts (approved and pending)
 */
export const getVendorBlogPosts = async (vendorId) => {
  try {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => doc.data());

    return { success: true, data: posts };
  } catch (error) {
    console.error('Error getting vendor posts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get posts pending approval (Admin only)
 */
export const getPendingBlogPosts = async () => {
  try {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => doc.data());

    return { success: true, data: posts };
  } catch (error) {
    console.error('Error getting pending posts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Approve blog post (Admin)
 */
export const approveBlogPost = async (postId, reviewNotes = '') => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    
    await updateDoc(postRef, {
      status: 'approved',
      approvedAt: Timestamp.now(),
      reviewNotes,
      updatedAt: Timestamp.now(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error approving blog post:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reject blog post (Admin)
 */
export const rejectBlogPost = async (postId, rejectionReason) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    
    await updateDoc(postRef, {
      status: 'rejected',
      rejectionReason,
      rejectedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error rejecting blog post:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Increment view count for post
 */
export const incrementPostViewCount = async (postId) => {
  try {
    const postRef = doc(db, BLOG_COLLECTION, postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      return { success: false, error: 'Post not found' };
    }

    const currentCount = postSnap.data().viewCount || 0;
    
    await updateDoc(postRef, {
      viewCount: currentCount + 1,
    });

    return { success: true };
  } catch (error) {
    console.error('Error incrementing view count:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Search blog posts
 */
export const searchBlogPosts = async (searchTerm) => {
  try {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const allPosts = querySnapshot.docs.map(doc => doc.data());

    // Client-side search
    const filtered = allPosts.filter(post =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return { success: true, data: filtered };
  } catch (error) {
    console.error('Error searching blog posts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get posts by category
 */
export const getBlogPostsByCategory = async (category) => {
  try {
    // First get approved posts, then filter by category on client-side to avoid composite index
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs
      .map(doc => doc.data())
      .filter(post => post.category === category);

    return { success: true, data: posts };
  } catch (error) {
    console.error('Error getting posts by category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get blog statistics (Admin)
 */
export const getBlogStatistics = async () => {
  try {
    const allPostsSnap = await getDocs(collection(db, BLOG_COLLECTION));
    
    const stats = {
      totalPosts: allPostsSnap.size,
      approved: 0,
      pending: 0,
      rejected: 0,
      totalViews: 0,
    };

    allPostsSnap.forEach(doc => {
      const post = doc.data();
      if (post.status === 'approved') stats.approved++;
      if (post.status === 'pending') stats.pending++;
      if (post.status === 'rejected') stats.rejected++;
      stats.totalViews += post.viewCount || 0;
    });

    return { success: true, data: stats };
  } catch (error) {
    console.error('Error getting blog statistics:', error);
    return { success: false, error: error.message };
  }
};

export default {
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  getBlogPost,
  getApprovedBlogPosts,
  getTrendingBlogPosts,
  getNewestBlogPosts,
  getVendorBlogPosts,
  getPendingBlogPosts,
  approveBlogPost,
  rejectBlogPost,
  incrementPostViewCount,
  searchBlogPosts,
  getBlogPostsByCategory,
  getBlogStatistics,
};
